import { useConfig } from "@/react/story/providers/ConfigContext";
import { LocalesContext } from "@/react/story/providers/LocalesContext";
import { useStory } from "@/react/story/providers/StoryContext";
import type { Locale } from "@/react/story/types";
import { type ReactNode, useMemo, useState } from "react";

type Props = {
    defaultLocale: Locale;
    presetLocales: Locale[];
    children: ReactNode;
}

export default function LocalesProvider(props: Props) {
    const Config = useConfig();
    const Story = useStory();

    // Mémorisé pour la même raison que dans `FontsProvider` : `all` est une dépendance du
    // `useMemo` du contexte, et `?? []` rendrait un tableau neuf à chaque rendu.
    const all = useMemo(() => Story.story.locales ?? [], [ Story.story.locales ]);

    // `defaultLocale` est la langue du visiteur (`Accept-Language`) choisie parmi celles de la
    // story ; l'éditeur, lui, s'ouvre toujours sur la première langue déclarée.
    const initial = Config.isOnline && all.includes(props.defaultLocale)
        ? props.defaultLocale
        : all[0] ?? props.defaultLocale;

    const [ locale, setLocale ] = useState(initial);

    const goto = (l: Locale | undefined) => {
        setLocale(l ?? initial);
    };

    const context = useMemo(() => ({
        all,
        count: all.length,
        goto,
        current: locale,
        set: (locales: Locale[]) => {
            const orderId1 = all.join("/");
            const orderId2 = locales.join("/");
            if (orderId1 !== orderId2) {
                return Story.patch("locales", locales);
            }
            return Promise.resolve();
        },
        add: (locale: Locale) => {
            return Story.patch("locales", [ ...all, locale ]);
        },
        remove: (locale: Locale) => {
            return Story.patch("locales", all.filter(l => l !== locale));
        },
        presets: props.presetLocales,
    }), [ all, locale, props.presetLocales ]);

    return (
        <LocalesContext.Provider value={context}>
            {props.children}
        </LocalesContext.Provider>
    );
}
